import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  FormControlLabel,
  Switch,
  Pagination,
  Tabs,
  Tab,
  InputAdornment,
  Checkbox
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Person,
  Upload,
  Download,
  Search,
  FilterList,
  Refresh as RefreshIcon,
  FileDownload as FileDownloadIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { useAppStore } from '../stores/useAppStore';
import { UserImport } from '../components/UserImport';
import { PositionsManagementTab } from '../components/PositionsManagementTab';
import { userService, type User, type CreateUserData, type UpdateUserData } from '../services/userService';
import { isAdminOrApprover, isStudent } from '../utils/rbac';
import { useSnackbar } from '../hooks/useSnackbar';
import { ToastNotification } from '../components/ToastNotification';
import { ExportDialog } from '../components/ExportDialog';
import { type ExportColumn } from '../services/exportService';
import { logger } from '../utils/logger';
import { positionService, type Position, type CreatePositionData, type UpdatePositionData } from '../services/positionService';


// User interface is imported from userService

export const UserManagementPage: React.FC = () => {
  const { user } = useAppStore();
  const userRoles = user?.roles || [];
  const isSuperAdmin = user?.isSuperAdmin === true;
  const canEditUserStatus = isAdminOrApprover(userRoles) || isSuperAdmin; // Admin/Approver/Super-admin can edit user status
  const canExport = !isStudent(userRoles);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    cmsUserId: '',
    fullName: '',
    email: '',
    phoneNumber: '',
    address: '',
    roles: [] as string[],
    password: '',
    notes: '',
    isActive: true // User status: true = Hoạt động, false = Tạm dừng
  });

  const usernameFromEmail = (email: string | null | undefined): string => {
    const e = String(email || '').trim();
    if (!e) return '';
    const at = e.indexOf('@');
    const base = (at > 0 ? e.slice(0, at) : e).trim();
    return base;
  };

  const getUsername = (u: Pick<User, 'email' | 'cmsUserId'>): string => {
    return usernameFromEmail(u.email) || (u.cmsUserId || '');
  };

  // Load users from API
  const loadUsers = async () => {
    try {
      logger.log('🔄 UserManagementPage - loadUsers() called');
      logger.log('🔄 UserManagementPage - Current users state:', users.length);
      logger.log('🔄 UserManagementPage - sessionStorage user:', sessionStorage.getItem('user'));
      
      setLoading(true);
      logger.log('🔄 UserManagementPage - Calling userService.getUsers()...');
      
      const usersData = await userService.getUsers();
      
      logger.log('✅ UserManagementPage - getUsers() returned:', usersData?.length || 0, 'users');
      logger.log('✅ UserManagementPage - Users data:', usersData?.map(u => ({
        id: u.id,
        cmsUserId: u.cmsUserId,
        fullName: u.fullName,
        email: u.email,
        dateCreated: u.dateCreated
      })) || []);
      
      if (!usersData || !Array.isArray(usersData)) {
        logger.error('❌ UserManagementPage - Invalid users data:', usersData);
        throw new Error('Invalid users data received');
      }
      
      logger.log('✅ UserManagementPage - Setting users state with', usersData.length, 'users');
      setUsers(usersData);
      logger.log('✅ UserManagementPage - Users state updated. New count:', usersData.length);
      
    } catch (error) {
      logger.error('❌ UserManagementPage - Error loading users:', error);
      logger.error('❌ UserManagementPage - Error details:', error instanceof Error ? error.message : error);
      showError(`Lỗi khi tải danh sách người dùng: ${error instanceof Error ? error.message : 'Unknown error'}`);
      // Clear users on error to avoid showing stale data
      setUsers([]);
    } finally {
      setLoading(false);
      logger.log('🔄 UserManagementPage - loadUsers() completed, loading set to false');
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleRefresh = () => {
    // Clear all filters and reload
    setSearchTerm('');
    setRoleFilter('');
    setStatusFilter('');
    setPage(1);
    loadUsers();
  };

  const filteredUsers = users.filter(user => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = (user.fullName || '').toLowerCase().includes(term) ||
                         (user.email || '').toLowerCase().includes(term) ||
                         (getUsername(user) || '').toLowerCase().includes(term) ||
                         (user.phoneNumber && user.phoneNumber.toLowerCase().includes(term)) ||
                         (user.cmsUserId && user.cmsUserId.toLowerCase().includes(term));
    const matchesRole = !roleFilter || user.roles.includes(roleFilter);
    const matchesStatus = !statusFilter || 
                          (statusFilter === 'active' && user.isActive) ||
                          (statusFilter === 'inactive' && !user.isActive);
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredUsers.length / pageSize);
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, roleFilter, statusFilter]);

  const handleCreateUser = () => {
    setFormData({
      cmsUserId: '',
      fullName: '',
      email: '',
      phoneNumber: '',
      address: '',
      roles: [],
      password: '',
      notes: '',
      isActive: true
    });
    setCreateDialogOpen(true);
  };

  const handleViewUser = (user: User) => {
    setSelectedUser(user);
    setViewDialogOpen(true);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setFormData({
      cmsUserId: user.cmsUserId || '',
      fullName: user.fullName,
      email: user.email,
      phoneNumber: user.phoneNumber || '',
      address: user.address || '',
      roles: user.roles,
      password: '',
      notes: user.notes || '',
      isActive: user.isActive !== undefined ? user.isActive : true
    });
    setEditDialogOpen(true);
  };

  const handleDeleteUser = async (userId: string) => {
    if (window.confirm('Bạn có chắc chắn muốn xóa người dùng này?')) {
      try {
        await userService.deleteUser(userId);
        // Reload users from DB to ensure data consistency
        logger.log('🔄 UserManagementPage - Reloading users from DB after delete...');
        await new Promise(resolve => setTimeout(resolve, 500));
        await loadUsers();
        logger.log('✅ UserManagementPage - Users reloaded after delete');
        showSuccess('Xóa người dùng thành công!');
      } catch (error) {
        logger.error('Error deleting user:', error);
        showError('Lỗi khi xóa người dùng');
      }
    }
  };

  const handleToggleUserStatus = async (userId: string, newStatus: boolean) => {
    try {
      logger.log(`🔄 UserManagementPage - Toggling user status: ${userId} to ${newStatus}`);
      
      await userService.updateUserStatus(userId, newStatus);
      
      // Reload users from DB to ensure data consistency
      await new Promise(resolve => setTimeout(resolve, 300));
      await loadUsers();
      
      // Show toast notification
      showSuccess(`Đã ${newStatus ? 'kích hoạt' : 'tạm dừng'} người dùng thành công!`);
      
      logger.log(`✅ UserManagementPage - User status updated to: ${newStatus}`);
    } catch (error) {
      logger.error('❌ UserManagementPage - Error toggling user status:', error);
      showError(`Lỗi khi ${newStatus ? 'kích hoạt' : 'tạm dừng'} người dùng: ${error instanceof Error ? error.message : 'Unknown error'}`);
      
      // Reload to revert UI state
      await loadUsers();
    }
  };

  const handleSaveUser = async () => {
    const wasEditing = editDialogOpen;
    try {
      if (createDialogOpen) {
        // Create new user via API
        logger.log('🆕 UserManagementPage - handleSaveUser() - Creating new user');
        logger.log('🆕 UserManagementPage - Form data:', formData);
        const userData: CreateUserData = {
          cmsUserId: formData.cmsUserId || usernameFromEmail(formData.email) || `USER${Date.now()}`,
          fullName: formData.fullName,
          email: formData.email,
          phoneNumber: formData.phoneNumber || undefined,
          address: formData.address || undefined,
          roles: formData.roles,
          password: formData.password || undefined,
          notes: formData.notes || undefined
        };
        logger.log('🆕 UserManagementPage - Calling userService.createUser() with:', userData);
        const newUser = await userService.createUser(userData);
        logger.log('✅ UserManagementPage - createUser() returned:', newUser);
        logger.log('✅ UserManagementPage - User created successfully:', {
          id: newUser.id,
          cmsUserId: newUser.cmsUserId,
          fullName: newUser.fullName,
          email: newUser.email
        });
        // Reload users from DB to ensure data consistency
        logger.log('🔄 UserManagementPage - Reloading users from DB after create...');
        // Wait a bit to ensure DB transaction is committed
        await new Promise(resolve => setTimeout(resolve, 500));
        await loadUsers();
        logger.log('✅ UserManagementPage - Users reloaded after create, current count:', users.length);
        setCreateDialogOpen(false);
      } else if (editDialogOpen && selectedUser) {
        // Update existing user via API
        const userData: UpdateUserData = {
          cmsUserId: formData.cmsUserId || selectedUser.cmsUserId || undefined,
          fullName: formData.fullName,
          email: formData.email,
          phoneNumber: formData.phoneNumber || undefined,
          address: formData.address || undefined,
          roles: formData.roles,
          password: formData.password || undefined,
          notes: formData.notes || undefined,
          isActive: canEditUserStatus ? formData.isActive : undefined // Only send isActive if user has permission
        };
        await userService.updateUser(selectedUser.id, userData);
        // Reload users from DB to ensure data consistency
        logger.log('🔄 UserManagementPage - Reloading users from DB after update...');
        await new Promise(resolve => setTimeout(resolve, 500));
        await loadUsers();
        logger.log('✅ UserManagementPage - Users reloaded after update');
        setEditDialogOpen(false);
      }
      
      // Reset form and close dialog
      setFormData({
        cmsUserId: '',
        fullName: '',
        email: '',
        phoneNumber: '',
        address: '',
        roles: [],
        password: '',
        notes: '',
        isActive: true
      });
      setCreateDialogOpen(false);
      setEditDialogOpen(false);
      setSelectedUser(null);
      
      // Show success message
      showSuccess(wasEditing ? 'Cập nhật người dùng thành công!' : 'Tạo người dùng thành công!');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Có lỗi xảy ra khi lưu người dùng';
      showError(errorMessage);
    }
  };


  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedUserIds(paginatedUsers.map(u => u.id));
    } else {
      setSelectedUserIds([]);
    }
  };

  const handleSelectUser = (userId: string) => {
    setSelectedUserIds(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleBulkDeleteClick = () => {
    if (selectedUserIds.length === 0) return;
    setBulkDeleteDialogOpen(true);
  };

  const handleBulkDeleteConfirm = async () => {
    try {
      const response = await userService.bulkDeleteUsers(selectedUserIds);
      
      if (response.success) {
        showSuccess(`Đã xóa ${response.deletedCount} người dùng thành công`);
        setBulkDeleteDialogOpen(false);
        setSelectedUserIds([]);
        await new Promise(resolve => setTimeout(resolve, 500));
        await loadUsers();
      } else {
        showError(response.message || 'Không thể xóa người dùng');
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Không thể xóa người dùng');
    }
  };

  const handleBulkDeleteCancel = () => {
    setBulkDeleteDialogOpen(false);
  };

  const getRoleColor = (role: string) => {
    switch (role.toLowerCase()) {
      case 'admin':
        return 'error';
      case 'approver':
        return 'secondary'; // Màu tím cho Ban Giám Hiệu
      case 'librarian':
        return 'primary';
      case 'teacher':
        return 'success';
      case 'student':
        return 'info';
      default:
        return 'default';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role.toLowerCase()) {
      case 'admin':
        return 'Quản trị viên';
      case 'approver':
        return 'BGH'; // Ban Giám Hiệu
      case 'librarian':
        return 'Thủ thư';
      case 'teacher':
        return 'Giáo viên';
      case 'student':
        return 'Học sinh';
      default:
        return role;
    }
  };

  const handleExportUsers = async (dateFrom: Date | null, dateTo: Date | null): Promise<User[]> => {
    const rows = await userService.exportUsers(dateFrom, dateTo);
    // Add computed username for export
    return rows.map((u: any) => ({ ...u, username: getUsername(u) }));
  };

  const userExportColumns: ExportColumn[] = [
    { key: 'username', label: 'Username', width: 15 },
    { key: 'fullName', label: 'Họ và tên', width: 25 },
    { key: 'email', label: 'Email', width: 25 },
    { key: 'phoneNumber', label: 'Số điện thoại', width: 15 },
    { key: 'address', label: 'Địa chỉ', width: 30 },
    { key: 'roles', label: 'Vai trò', width: 20 },
    { key: 'isActive', label: 'Trạng thái', width: 12 },
    { key: 'dateCreated', label: 'Ngày tạo', width: 15 },
    { key: 'lastLogin', label: 'Lần đăng nhập cuối', width: 18 }
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Box>
      {/* Removed per request: no "QL người dùng" title/description */}

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Danh Sách Người Dùng" />
          <Tab label="Import" />
          <Tab label="Chức vụ" />
        </Tabs>
      </Paper>

      {activeTab === 0 && (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box />
            <Box sx={{ display: 'flex', gap: 2 }}>
              {canExport && (
                <Tooltip title="Xuất Excel" arrow>
                  <IconButton
                    color="primary"
                    onClick={() => setExportDialogOpen(true)}
                    aria-label="Xuất Excel"
                    size="small"
                  >
                    <FileDownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Tạo người dùng mới" arrow>
                <IconButton
                  color="primary"
                  onClick={handleCreateUser}
                  aria-label="Tạo người dùng mới"
                  size="small"
                >
                  <Add fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Box display="flex" gap={2} alignItems="center">
              <TextField
                placeholder="Tìm kiếm theo tên, email, số điện thoại..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{ flexGrow: 1 }}
              />
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Vai trò</InputLabel>
                <Select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  label="Vai trò"
                >
                  <MenuItem value="">Tất cả vai trò</MenuItem>
                  <MenuItem value="Admin">Quản trị viên</MenuItem>
                  <MenuItem value="Librarian">Thủ thư</MenuItem>
                  <MenuItem value="Teacher">Giáo viên</MenuItem>
                  <MenuItem value="Student">Học sinh</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 180 }}>
                <InputLabel>Trạng thái</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Trạng thái"
                >
                  <MenuItem value="">Tất cả trạng thái</MenuItem>
                  <MenuItem value="active">Hoạt động</MenuItem>
                  <MenuItem value="inactive">Không hoạt động</MenuItem>
                </Select>
              </FormControl>
              <Tooltip title="Làm mới" arrow>
                <IconButton
                  color="primary"
                  onClick={handleRefresh}
                  aria-label="Làm mới"
                  size="small"
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {canEditUserStatus && selectedUserIds.length > 0 && (
                <Tooltip title={`Xóa (${selectedUserIds.length})`} arrow>
                  <IconButton
                    color="error"
                    onClick={handleBulkDeleteClick}
                    aria-label="Xóa"
                    size="small"
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Paper>

          <TableContainer component={Paper} sx={{ minHeight: 200, width: '100%' }}>
            <Table sx={{ width: '100%', tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: canEditUserStatus ? '22%' : '24%' }}>Người dùng</TableCell>
                  <TableCell sx={{ width: '18%' }}>Email</TableCell>
                  <TableCell sx={{ width: '12%' }}>Số điện thoại</TableCell>
                  <TableCell sx={{ width: '16%' }}>Vai trò</TableCell>
                  <TableCell sx={{ width: '12%' }}>Trạng thái</TableCell>
                  <TableCell align="center" sx={{ width: '15%' }}>Thao tác</TableCell>
                  {canEditUserStatus && (
                    <TableCell padding="none" align="center" sx={{ width: '14px', minWidth: '14px', maxWidth: '14px', padding: '0px !important' }}>
                      <Checkbox
                        indeterminate={selectedUserIds.length > 0 && selectedUserIds.length < paginatedUsers.length}
                        checked={paginatedUsers.length > 0 && selectedUserIds.length === paginatedUsers.length}
                        onChange={handleSelectAll}
                        size="small"
                        sx={{ padding: '0px', '& svg': { fontSize: '20px' } }}
                      />
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={canEditUserStatus ? 7 : 6} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : paginatedUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={canEditUserStatus ? 7 : 6} align="center" sx={{ py: 8 }}>
                      <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                        <Typography variant="h6" color="text.secondary">
                          {searchTerm || roleFilter || statusFilter ? 
                            'Không tìm thấy người dùng nào phù hợp với điều kiện tìm kiếm' : 
                            'Chưa có người dùng nào trong hệ thống'
                          }
                        </Typography>
                        {(searchTerm || roleFilter || statusFilter) && (
                          <Typography variant="body2" color="text.secondary">
                            Thử thay đổi từ khóa tìm kiếm hoặc chọn bộ lọc khác
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedUsers.map((user) => {
                    const isSelected = selectedUserIds.includes(user.id);
                    
                    return (
                    <TableRow 
                      key={user.id}
                      onClick={() => handleViewUser(user)}
                      sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                    >
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {user.fullName}
                      </Typography>
                      {getUsername(user) && (
                        <Typography variant="caption" color="text.secondary">
                          {getUsername(user)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.phoneNumber || 'Chưa có'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        {user.roles.map((role, index) => {
                          const roleColor = getRoleColor(role);
                          const isApprover = role.toLowerCase() === 'approver';
                          let dotColor = '#9e9e9e'; // default gray
                          if (isApprover) {
                            dotColor = '#9c27b0'; // purple
                          } else {
                            switch (roleColor) {
                              case 'error':
                                dotColor = '#f44336'; // red
                                break;
                              case 'primary':
                                dotColor = '#1976d2'; // blue
                                break;
                              case 'success':
                                dotColor = '#4caf50'; // green
                                break;
                              case 'info':
                                dotColor = '#00acc1'; // cyan
                                break;
                              default:
                                dotColor = '#9e9e9e';
                            }
                          }
                          return (
                            <Tooltip key={index} title={getRoleLabel(role)} arrow>
                              <Box
                                sx={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: '50%',
                                  backgroundColor: dotColor,
                                  display: 'inline-block',
                                  cursor: 'help'
                                }}
                              />
                            </Tooltip>
                          );
                        })}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title={canEditUserStatus ? 'Bật/tắt tài khoản' : 'Chỉ Admin/BGH/Super-admin được thay đổi'} arrow>
                          <Switch
                            checked={user.isActive}
                            disabled={!canEditUserStatus}
                            onChange={(e) => {
                              e.stopPropagation();
                              if (!canEditUserStatus) return;
                              handleToggleUserStatus(user.id, e.target.checked);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            color="primary"
                            size="small"
                            inputProps={{ 'aria-label': 'Trạng thái người dùng' }}
                          />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditUser(user);
                        }}
                        title="Chỉnh sửa"
                        disableFocusRipple
                        disableRipple
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteUser(user.id);
                        }}
                        title="Xóa"
                        color="error"
                        disableFocusRipple
                        disableRipple
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                    {canEditUserStatus && (
                      <TableCell padding="none" align="center" sx={{ width: '14px', minWidth: '14px', maxWidth: '14px', padding: '0px !important' }}>
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleSelectUser(user.id)}
                          size="small"
                          sx={{ padding: '0px', '& svg': { fontSize: '20px' } }}
                        />
                      </TableCell>
                    )}
                  </TableRow>
                  );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {activeTab === 1 && <UserImport />}

      {activeTab === 2 && (
        <PositionsManagementTab />
      )}

      {/* Create/Edit User Dialog */}
      <Dialog 
        open={createDialogOpen || editDialogOpen} 
        onClose={() => {
          setCreateDialogOpen(false);
          setEditDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {createDialogOpen ? 'Tạo người dùng mới' : 'Chỉnh sửa người dùng'}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ pt: 1 }}>
            {isSuperAdmin && editDialogOpen ? (
              <TextField
                fullWidth
                label="Username"
                value={formData.cmsUserId}
                onChange={(e) => setFormData(prev => ({ ...prev, cmsUserId: e.target.value }))}
                margin="normal"
                required
                helperText="Super-admin có thể chỉnh Username. Lưu ý: Username phải là duy nhất trong thư viện của trường."
              />
            ) : (
              <TextField
                fullWidth
                label="Username"
                value={usernameFromEmail(formData.email) || formData.cmsUserId}
                margin="normal"
                required
                helperText="Username được lấy từ phần đầu của email (ví dụ: nguyenthimaixuan@gmail.com → nguyenthimaixuan)"
                disabled
              />
            )}
            
            <TextField
              fullWidth
              label="Họ và tên"
              value={formData.fullName}
              onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))}
              margin="normal"
              required
            />
            
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              margin="normal"
              required
            />
            
            <TextField
              fullWidth
              label="Số điện thoại"
              value={formData.phoneNumber}
              onChange={(e) => setFormData(prev => ({ ...prev, phoneNumber: e.target.value }))}
              margin="normal"
            />
            
            <TextField
              fullWidth
              label="Địa chỉ"
              value={formData.address}
              onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
              margin="normal"
              multiline
              rows={2}
            />

            {createDialogOpen && (
              <TextField
                fullWidth
                label="Mật khẩu"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                margin="normal"
                required
              />
            )}
            
            {editDialogOpen && isSuperAdmin && (
              <TextField
                fullWidth
                label="Mật khẩu mới"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                margin="normal"
                helperText="Để trống nếu không muốn đổi mật khẩu."
              />
            )}

            <FormControl fullWidth margin="normal">
              <InputLabel>Vai trò</InputLabel>
              <Select
                multiple
                value={formData.roles}
                onChange={(e) => setFormData(prev => ({ ...prev, roles: e.target.value as string[] }))}
                label="Vai trò"
              >
                <MenuItem value="Admin">Quản trị viên</MenuItem>
                <MenuItem value="Approver">BGH (Ban Giám Hiệu)</MenuItem>
                <MenuItem value="Librarian">Thủ thư</MenuItem>
                <MenuItem value="Teacher">Giáo viên</MenuItem>
                <MenuItem value="Student">Học sinh</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="Ghi chú"
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              margin="normal"
              multiline
              rows={2}
            />

            {/* Only show status toggle for Admin/Approver when editing */}
            {editDialogOpen && canEditUserStatus && (
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                    color="primary"
                  />
                }
                label={formData.isActive ? 'Hoạt động' : 'Tạm dừng'}
                sx={{ mt: 2, mb: 1 }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCreateDialogOpen(false);
            setEditDialogOpen(false);
          }}>
            Hủy
          </Button>
          <Button onClick={handleSaveUser} variant="contained">
            {createDialogOpen ? 'Tạo' : 'Lưu'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onClose={handleBulkDeleteCancel}>
        <DialogTitle>Xác nhận xóa nhiều người dùng</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Bạn có chắc chắn muốn xóa {selectedUserIds.length} người dùng không?
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Hành động này không thể hoàn tác. Tất cả dữ liệu liên quan đến các người dùng này sẽ bị xóa vĩnh viễn.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBulkDeleteCancel}>Hủy</Button>
          <Button onClick={handleBulkDeleteConfirm} color="error" variant="contained">
            Xóa
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Detail Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h5" component="div">
            {selectedUser?.fullName}
          </Typography>
          {selectedUser?.cmsUserId && (
            <Typography variant="subtitle1" color="text.secondary" component="div">
              Username: {getUsername(selectedUser)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3, mt: 1 }}>
            <Box>
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Thông tin cơ bản
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Họ và tên
                    </Typography>
                    <Typography variant="body1">{selectedUser?.fullName || 'N/A'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1">{selectedUser?.email || 'N/A'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Số điện thoại
                    </Typography>
                    <Typography variant="body1">{selectedUser?.phoneNumber || 'Chưa có'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Địa chỉ
                    </Typography>
                    <Typography variant="body1">{selectedUser?.address || 'Chưa có'}</Typography>
                  </Box>
                  {getUsername(selectedUser || { email: '', cmsUserId: '' } as any) && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Username
                      </Typography>
                      <Typography variant="body1">{getUsername(selectedUser as any)}</Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
              {selectedUser?.notes && (
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Ghi chú
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedUser.notes}
                  </Typography>
                </Paper>
              )}
            </Box>
            <Box>
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Vai trò
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
                  {selectedUser?.roles && selectedUser.roles.length > 0 ? (
                    selectedUser.roles.map((role, index) => {
                      const roleColor = getRoleColor(role);
                      const isApprover = role.toLowerCase() === 'approver';
                      let dotColor = '#9e9e9e';
                      if (isApprover) {
                        dotColor = '#9c27b0';
                      } else {
                        switch (roleColor) {
                          case 'error':
                            dotColor = '#f44336';
                            break;
                          case 'primary':
                            dotColor = '#1976d2';
                            break;
                          case 'success':
                            dotColor = '#4caf50';
                            break;
                          case 'info':
                            dotColor = '#00acc1';
                            break;
                          default:
                            dotColor = '#9e9e9e';
                        }
                      }
                      return (
                        <Tooltip key={index} title={getRoleLabel(role)} arrow>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: dotColor,
                              display: 'inline-block',
                              cursor: 'help'
                            }}
                          />
                        </Tooltip>
                      );
                    })
                  ) : (
                    <Typography variant="body2" color="text.secondary">Chưa có vai trò</Typography>
                  )}
                </Box>
                {/* Chú thích màu sắc vai trò */}
                <Box sx={{ mt: 2, p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Typography variant="caption" fontWeight="bold" display="block" sx={{ mb: 1 }}>
                    Chú thích vai trò:
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#f44336', flexShrink: 0 }} />
                      <Typography variant="caption">Quản trị viên (Admin)</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#9c27b0', flexShrink: 0 }} />
                      <Typography variant="caption">Ban Giám Hiệu (BGH)</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#1976d2', flexShrink: 0 }} />
                      <Typography variant="caption">Thủ thư (Librarian)</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#4caf50', flexShrink: 0 }} />
                      <Typography variant="caption">Giáo viên (Teacher)</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#00acc1', flexShrink: 0 }} />
                      <Typography variant="caption">Học sinh (Student)</Typography>
                    </Box>
                  </Box>
                </Box>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Trạng thái
                </Typography>
                <Chip
                  label={selectedUser?.isActive ? 'Hoạt động' : 'Tạm dừng'}
                  color={selectedUser?.isActive ? 'success' : 'error'}
                  size="small"
                />
                {selectedUser?.dateCreated && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Ngày tạo
                    </Typography>
                    <Typography variant="body2">
                      {new Date(selectedUser.dateCreated).toLocaleDateString('vi-VN')}
                    </Typography>
                  </Box>
                )}
                {selectedUser?.lastLogin && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Đăng nhập cuối
                    </Typography>
                    <Typography variant="body2">
                      {new Date(selectedUser.lastLogin).toLocaleDateString('vi-VN')}
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Đóng</Button>
          {selectedUser && (
            <Button onClick={() => {
              setViewDialogOpen(false);
              handleEditUser(selectedUser);
            }} variant="contained">
              Chỉnh sửa
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <ExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        onExport={handleExportUsers}
        columns={userExportColumns}
        fileName="Danh_sach_nguoi_dung"
        sheetName="Người dùng"
        title="Xuất danh sách người dùng ra Excel"
        description="Chọn khoảng thời gian để xuất dữ liệu người dùng. Dữ liệu sẽ được xuất theo ngày tạo tài khoản."
        requireDateRange={true}
      />

      <ToastNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={hideSnackbar}
      />
    </Box>
  );
};

export default UserManagementPage;
